import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  connectSocialMedia,
  getPlaceById,
  getPlacesByName,
  retrieveUserSocialMedia,
} from "services/SauceService";
import { ReactComponent as CloseIcon } from "assets/images/trash-icon.svg";
import { formatNumberKMBT } from "pages/influencers/InfluencerCard";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import { optionsWeekDay } from "pages/collab/FieldDay";
import { useDispatch, useSelector } from "react-redux";
import { BusinessState, getBusinessInfo } from "redux-context/business";
import { CollabsState } from "redux-context/collabs";
import { useNavigate } from "react-router-dom";
import bookingAvailability from "../collab/weekHoursDefault.json";
import { ArrowRightOutlined, CheckCircleFilled } from "@ant-design/icons";
import MetaButton from "components/connect-meta/MetaButton";
import { handleFacebookLogin } from "helpers/facebook-auth";
import { formatHour } from "helpers/working-hours";

function isOpen24By7(opening_hours?: {
  periods?: any[];
  weekday_text?: string[];
}): boolean {
  if (!opening_hours) return false;

  // Checa por texto "Open 24 hours" nos 7 dias
  const allDays24h =
    opening_hours.weekday_text?.length === 7 &&
    opening_hours.weekday_text.every((text) =>
      text.toLowerCase().includes("open 24 hours")
    );

  // Checa se periods tem só 1 item com open às 00:00 no domingo (0)
  const period24h =
    opening_hours.periods?.length === 1 &&
    opening_hours.periods[0].open?.day === 0 &&
    opening_hours.periods[0].open?.time === "0000" &&
    !opening_hours.periods[0].close;

  return allDays24h || period24h;
}

interface Address {
  formatted_address: string;
  formatted_phone_number: string;
  price_level: number;
  rating: number;
  types: string[];
  url: string;
  website: string;
  countryCode: string;
  name: string;
  user_ratings_total: number;
  photos: {
    height: number;
    html_attributions: string[];
    url: string;
    width: number;
  }[];
  opening_hours: {
    open_now: boolean;
    periods: {
      close: {
        day: number;
        time: string;
      };
      open: {
        day: number;
        time: string;
      };
    }[];
    weekday_text: string[];
  };
  address_components: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
    viewport: {
      northeast: {
        lat: number;
        lng: number;
      };
      southwest: {
        lat: number;
        lng: number;
      };
    };
  };
}

// function generateGoogleTypesMap(type: string): string {
//   const title = type
//     .split("_")
//     .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
//     .join(" ");
//   return title;
// }

interface RestaurantInfoProps {
  setSubmitBlocked: (value: boolean) => void;
  setLocationInfo: (value: any) => void;
  locationInfo: any;
}

enum SocialMedia {
  INSTAGRAM = "Instagram",
  TIKTOK = "Tiktok",
}

interface SocialMediaInfo {
  followers: number;
  name: string;
  profileImage: string;
  username: string;
  bio: string;
  postsCount: number;
  followings: number;
}

const RestaurantInfo: React.FC<RestaurantInfoProps> = (props) => {
  const { setSubmitBlocked, setLocationInfo, locationInfo } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const form = Form.useFormInstance();
  const [loading, setLoading] = useState<boolean>(false);
  const { business } = useSelector((state: BusinessState) => state.business);
  const { numbers } = useSelector((state: CollabsState) => state.collabs);
  const [loadingSelect, setLoadingSelect] = useState<boolean>(false);
  const [showTiktok, setShowTiktok] = useState<boolean>(false);
  const [places, setPlaces] = useState<any[]>([]);
  const [instagramInfo, setInstagramInfo] = useState<SocialMediaInfo>();
  const [tiktokInfo, setTiktokInfo] = useState<SocialMediaInfo>();
  const [addressInfo, setAddressInfo] = useState<Address>();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const socialMediaConencted = useMemo(() => {
    return business?.socialMediasConnected?.some((socialMedia) =>
      ["instagram", "meta"].includes(
        socialMedia?.socialMediaName?.toLowerCase()
      )
    );
  }, [business]);

  const invalidAddressType = useMemo(() => {
    const invalidTypes = [
      "street_address",
      "route",
      "intersection",
      "political",
      "sublocality",
      "sublocality_level_1",
      "sublocality_level_2",
      "locality",
      "administrative_area_level_1",
      "administrative_area_level_2",
      "country",
      "premise",
    ];
    return invalidTypes.includes(addressInfo?.types?.[0] || "");
  }, [addressInfo]);

  const getLocation = useCallback(async (locationName: string) => {
    setLoadingSelect(true);
    const response: any = await getPlacesByName({ name: locationName });
    setPlaces(
      response.map((place: any) => {
        const address = place.formatted_address.split(",");
        const number = address[0].split(" ")[0];
        const street = address[0].split(" ").slice(1).join(" ");
        const city = address[1] ? address[1].trim() : "";

        return {
          value: place.place_id,
          label: `${place.name} - ${number} ${street}, ${city}`,
          ...place,
        };
      })
    );
    setLoadingSelect(false);
  }, []);

  const handleSearch = (newValue: string) => {
    if (newValue) getLocation(newValue);
  };

  const handleChange = async (_: string, option: any) => {
    const response: any = await getPlaceById(option.place_id, true);

    const allowedCoutnries = ["US", "CA", "GB"];

    if (!allowedCoutnries.includes(response?.countryCode)) {
      form.setFieldValue("yourBusiness", undefined);
      message.error("Only US, Canada and GB are allowed");
      return;
    }

    const address = response.formatted_address.split(",");
    const number = address[0].split(" ")[0];
    const street = address[0].split(" ").slice(1).join(" ");
    const city = address[1] ? address[1].trim() : "";

    const formattedAddress = {
      ...option,
      ...response,
      formatted_address: `${number} ${street}, ${city}`,
    };
    setAddressInfo(formattedAddress);

    const isAlwaysOpen = isOpen24By7(response?.opening_hours);
    const venueWorkingHours = isAlwaysOpen
      ? bookingAvailability
      : optionsWeekDay
          .map((day) => {
            const periods = response?.opening_hours?.periods?.filter(
              (p: any) => optionsWeekDay[p.open.day].value === day.value
            );
            if (periods && periods.length > 0) {
              return periods.map((period: any) => {
                const startTime = formatHour(period.open.time);
                const endTime = formatHour(period.close?.time || "10:00 PM");
                const open = true;
                return { dayOfWeek: day.value, startTime, endTime, open };
              });
            } else {
              return [
                {
                  dayOfWeek: day.value,
                  startTime: "08:00 AM",
                  endTime: "18:00 AM",
                  open: false,
                },
              ];
            }
          })
          .flat();

    form.setFieldValue("venueWorkingHours", venueWorkingHours);
    form.setFieldValue("placeAddress", option?.formatted_address);
    form.setFieldValue("placeName", option?.name);
    form.setFieldValue("placeLatitude", option?.geometry?.location?.lat);
    form.setFieldValue("placeLongitude", option?.geometry?.location?.lng);
    form.setFieldValue("placeId", option?.place_id);
    setLocationInfo(option);
  };

  const onChangeInstagram = () => {
    const value = form.getFieldValue("instagramUserName");
    if (value) onSearchSocial(value, SocialMedia.INSTAGRAM);
  };

  const onChangeTiktok = () => {
    const value = form.getFieldValue("tikTokUserName");
    if (value) onSearchSocial(value, SocialMedia.TIKTOK);
  };

  const onSearchSocial = async (value: string, socialMedia: SocialMedia) => {
    const clearField = () => {
      if (socialMedia === SocialMedia.INSTAGRAM)
        form.setFieldValue("instagramUserName", "");
      if (socialMedia === SocialMedia.TIKTOK)
        form.setFieldValue("tikTokUserName", "");
    };
    const clearInfo = () => {
      if (socialMedia === SocialMedia.INSTAGRAM) setInstagramInfo(undefined);
      if (socialMedia === SocialMedia.TIKTOK) setTiktokInfo(undefined);
    };

    const currentValue =
      socialMedia === SocialMedia.INSTAGRAM ? instagramInfo : tiktokInfo;
    if (value && value !== currentValue?.username) {
      setSubmitBlocked(true);
      setLoading(true);
      clearInfo();
      const params = {
        username: value,
        socialMedia,
      };
      try {
        const response: any = await retrieveUserSocialMedia(params);

        if (response?.profileImage) {
          if (socialMedia === SocialMedia.INSTAGRAM) {
            setInstagramInfo(response);
            form.setFieldValue("instagramUserName", response.username);
          }
          if (socialMedia === SocialMedia.TIKTOK) {
            setTiktokInfo(response);
            form.setFieldValue("tikTokUserName", response.username);
          }
        } else {
          message.error("Account not found, try again");
          clearField();
        }
      } catch (error) {
        message.error("An error occurred, try again later");
        clearField();
      }
    }
    setLoading(false);
    setSubmitBlocked(false);
  };

  const onClickRemoveTiktok = () => {
    setShowTiktok(true);
    form.setFieldValue("tiktok", undefined);
    setTiktokInfo(undefined);
  };

  const handleFacebookResponse = async (response: any) => {
    if (response.status === "connected") {
      const businessId = localStorage.getItem("businessId");
      const accessToken = response.authResponse.accessToken;
      setLoading(true);
      try {
        await connectSocialMedia({
          businessId,
          accessToken,
        });
        setLoading(false);
        dispatch(getBusinessInfo());
        message.success("Meta Business connected successfully");
      } catch (error: any) {
        console.log(error);
        message.error(
          error?.response?.data?.message || "Error connecting Meta Business"
        );
        setLoading(false);
      }
    } else {
      console.log("User is not logged in with Facebook");
    }
  };

  const onClickSigninMeta = () => {
    handleFacebookLogin(handleFacebookResponse);
  };

  const filterListDoubleOrEmpty = (
    list: { label: string; value: any }[],
    item: { label: string; value: any }
  ) => {
    if (!item) return list;

    return [...list, item].filter(
      (item, index, self) =>
        item.value !== undefined &&
        index === self.findIndex((t) => t.label === item.label)
    );
  };

  useEffect(() => {
    if ((numbers?.totalCollabs || 0) > 0) {
      navigate("/");
    }
  }, [numbers, navigate]);

  useEffect(() => {
    if (business?.socialMediasConnected?.length) {
      const metaOrInstagramConnected = business?.socialMediasConnected?.find(
        (socialMedia) =>
          ["instagram", "meta"].includes(
            socialMedia?.socialMediaName?.toLowerCase()
          )
      );

      if (metaOrInstagramConnected) {
        form.setFieldValue(
          "instagramUserName",
          metaOrInstagramConnected?.accountName
        );
      }
    }
  }, [form, business]);

  return (
    <Spin spinning={loading}>
      <div>
        {business.businessName ? (
          <>
            <Typography.Title level={2}>
              Welcome, {business.businessName}!
            </Typography.Title>
            <Typography.Paragraph>
              Let's get you set up, it should take less than 5 minutes!
            </Typography.Paragraph>
          </>
        ) : (
          <Typography.Title level={2}>Brand info</Typography.Title>
        )}

        <Form.Item
          name="placeId"
          rules={[{ required: true, message: "Your brand is required" }]}
        >
          <Select
            placeholder="Find your brand"
            showSearch
            filterOption={false}
            onSearch={debounce(handleSearch, 500)}
            onSelect={handleChange}
            options={filterListDoubleOrEmpty(places, locationInfo)}
            loading={loadingSelect}
            allowClear
            className="select-height"
            optionRender={(option: any) => {
              // if (option.data)
              //   return (
              //     <>
              //       <div style={{ padding: "8px", cursor: "pointer" }}>
              //         {`${option.data.name} - ${option.data.formatted_address}`}
              //       </div>
              //     </>
              //   );
              // console.log(option?.label);
              return option?.label;
            }}
          />
        </Form.Item>
        {invalidAddressType && (
          <Form.Item
            name="placeName"
            label="Business name"
            rules={[{ required: true, message: "Business name is required" }]}
          >
            <Input placeholder="Please let us know the name of your business" />
          </Form.Item>
        )}
        {addressInfo && (
          <div className="box-social">
            {addressInfo.photos && addressInfo.photos[0] && (
              <img src={addressInfo.photos[0].url} alt="profile" />
            )}
            <div>
              <div>
                <div>
                  <span className="title bold">{addressInfo.name}</span>
                </div>
                <span className="title">Address: </span>
                <span className="title bold">
                  {addressInfo.formatted_address}
                </span>

                <div className="content-social">
                  {addressInfo.formatted_phone_number && (
                    <>
                      <span className="title">Phone: </span>
                      <span className="title bold">
                        {addressInfo.formatted_phone_number}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {/* {addressInfo.types && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginTop: 8,
                    }}
                  >
                    {addressInfo.types.map((type, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: "#f0f0f0",
                          padding: "4px 8px",
                          borderRadius: "8px",
                          fontSize: "16px",
                          fontWeight: "bold",
                        }}
                      >
                        {generateGoogleTypesMap(type)}
                      </span>
                    ))}
                  </div>
                </div>
              )} */}
            </div>
          </div>
        )}
        {!socialMediaConencted ? (
          <Row gutter={[6, 6]}>
            <Col xs={24}>
              <Form.Item
                name="instagramUserName"
                rules={[
                  { required: true, message: "Instagram handle is required" },
                ]}
              >
                <div className="custom-instagram-input-wrapper">
                  <span className="custom-prefix">@</span>
                  <Input
                    placeholder="Instagram handle"
                    className="custom-instagram-input"
                    onPressEnter={() => {
                      onChangeInstagram();
                      setShowTooltip(false);
                    }}
                    onMouseDown={() => setSubmitBlocked(true)}
                    onChange={debounce(() => {
                      setShowTooltip(true);
                    }, 3000)}
                  />
                  <Tooltip
                    title="click here or press enter to search"
                    open={showTooltip}
                  >
                    <Button
                      icon={<ArrowRightOutlined />}
                      className="custom-instagram-button"
                      type="text"
                      onClick={() => {
                        onChangeInstagram();
                        setShowTooltip(false);
                      }}
                    />
                  </Tooltip>
                </div>
              </Form.Item>
            </Col>
            <Col xs={24} style={{ textAlign: "center", fontWeight: 700 }}>
              OR
            </Col>
            <Col xs={24}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: 32,
                  marginTop: 12,
                }}
              >
                <MetaButton onClick={onClickSigninMeta} />
              </div>
            </Col>
            {/* <div
            style={{
              textAlign: "center",
              marginTop: 10,
              justifyContent: "center",
              flex: 1,
            }}
            className="label-strong"
          >
            OR click on
          </div>
          <Col xs={24} style={{ textAlign: "center" }}>
            <MetaButton
              // loading={loading}
              // className="label-strong"
              onClick={onClickSigninMeta}
              // style={{ width: 250, fontSize: 14 }}
            ></MetaButton>
          </Col> */}
          </Row>
        ) : (
          <Row gutter={[12, 12]}>
            <Col xs={24}>
              <div
                style={{
                  backgroundColor: "#E6F4EA", // Project's green background
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #B3D8C2", // Project's green border
                }}
              >
                <Typography.Text
                  style={{ color: "#155724", fontWeight: "bold" }}
                >
                  <span className="checkmark">
                    <CheckCircleFilled
                      style={{ color: "#155724", fontSize: 20, marginRight: 8 }}
                    />
                  </span>{" "}
                  Meta Business Connected
                </Typography.Text>
              </div>
            </Col>
          </Row>
        )}
        {instagramInfo && (
          <div className="box-social">
            <img src={instagramInfo.profileImage} alt="profile" />
            <div>
              <div className="content-social">
                <InstagramIcon />
                <span className="title bold">{instagramInfo.username}</span>
                <span className="title">{instagramInfo.name}</span>
              </div>
              <div className="content-social">
                <div className="content-social">
                  <span className="title bold">
                    {formatNumberKMBT(instagramInfo.followings)}
                  </span>
                  <span className="title">following</span>
                </div>
                <div className="content-social">
                  <span className="title bold">
                    {formatNumberKMBT(instagramInfo.followers)}
                  </span>
                  <span className="title">followers</span>
                </div>
                <div className="content-social">
                  <span className="title bold">
                    {formatNumberKMBT(instagramInfo.postsCount)}
                  </span>
                  <span className="title">posts</span>
                </div>
              </div>
              <div>
                <p>{instagramInfo.bio}</p>
              </div>
            </div>
          </div>
        )}

        {showTiktok && (
          <>
            <div className="social-media-input" style={{ marginTop: 24 }}>
              <Form.Item name="tikTokUserName">
                <div className="custom-instagram-input-wrapper">
                  <span className="custom-prefix">@</span>
                  <Input
                    placeholder="TikTok handle"
                    className="custom-instagram-input"
                    onPressEnter={onChangeTiktok}
                  />
                  <Button
                    icon={<ArrowRightOutlined />}
                    className="custom-instagram-button"
                    type="text"
                    onClick={onChangeTiktok}
                  />
                </div>
              </Form.Item>

              <CloseIcon
                onClick={() => setShowTiktok(false)}
                style={{ marginBottom: 24 }}
              />
            </div>
            {tiktokInfo && (
              <div className="box-social">
                <img src={tiktokInfo.profileImage} alt="profile" />
                <div>
                  <div className="content-social">
                    <TikTokIcon />
                    <span className="title bold">{tiktokInfo.username}</span>
                    <span className="title">{tiktokInfo.name}</span>
                  </div>
                  <div className="content-social">
                    <div className="content-social">
                      <span className="title bold">
                        {formatNumberKMBT(tiktokInfo.followings)}
                      </span>
                      <span className="title">following</span>
                    </div>
                    <div className="content-social">
                      <span className="title bold">
                        {formatNumberKMBT(tiktokInfo.followers)}
                      </span>
                      <span className="title">followers</span>
                    </div>
                    <div className="content-social">
                      <span className="title bold">
                        {formatNumberKMBT(tiktokInfo.postsCount)}
                      </span>
                      <span className="title">posts</span>
                    </div>
                  </div>
                  <div>
                    <p>{tiktokInfo.bio}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!showTiktok && (
          <div style={{ marginTop: 24 }}>
            Add another channel:{" "}
            {!showTiktok && (
              <span className="underline-span" onClick={onClickRemoveTiktok}>
                Tiktok
              </span>
            )}
          </div>
        )}
      </div>
    </Spin>
  );
};

export default RestaurantInfo;
